export type VaultDescriptionSegment =
  | {
      type: 'text'
      text: string
    }
  | {
      type: 'link'
      text: string
      href: string
    }

const markdownLinkRegex = /\[([^\]\n]+)\]\((https?:\/\/(?:[^\s()]|\([^\s()]*\))+?)\)/gi
const rawLinkRegex = /(?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/?#][^\s<]*)?/gi
const trailingPunctuationChars = new Set(['.', ',', '!', '?', ';', ':'])

// Strips trailing sentence punctuation off a matched link while keeping a
// balanced closing parenthesis (e.g. `…/Function_(mathematics)`); only an
// unmatched `)` such as in `(see example.com)` is treated as punctuation.
const splitTrailingPunctuation = (matchText: string) => {
  let end = matchText.length

  while (end > 0) {
    const char = matchText.charAt(end - 1)

    if (char === ')') {
      const kept = matchText.slice(0, end)
      const openCount = (kept.match(/\(/g) ?? []).length
      const closeCount = (kept.match(/\)/g) ?? []).length
      if (closeCount <= openCount) break
    } else if (!trailingPunctuationChars.has(char)) {
      break
    }

    end -= 1
  }

  return { linkText: matchText.slice(0, end), trailingPunctuation: matchText.slice(end) }
}

const toHref = (text: string) => {
  const lowercaseText = text.toLowerCase()

  return lowercaseText.startsWith('http://') || lowercaseText.startsWith('https://')
    ? text
    : `https://${text}`
}

const appendText = (segments: VaultDescriptionSegment[], text: string) => {
  if (!text) return

  const previous = segments.at(-1)
  if (previous?.type === 'text') {
    previous.text += text
  } else {
    segments.push({ type: 'text', text })
  }
}

const parseRawLinks = (text: string): VaultDescriptionSegment[] => {
  const segments: VaultDescriptionSegment[] = []
  let cursor = 0

  for (const match of text.matchAll(rawLinkRegex)) {
    const matchText = match[0]
    const matchIndex = match.index ?? 0

    appendText(segments, text.slice(cursor, matchIndex))

    // Skip matches that belong to an email address. Checking the char after the
    // match also covers dotted local parts like `first.last@example.com`, where
    // the bare-domain branch matches `first.last` before reaching the `@`.
    if (text[matchIndex - 1] === '@' || text[matchIndex + matchText.length] === '@') {
      appendText(segments, matchText)
      cursor = matchIndex + matchText.length
      continue
    }

    const { linkText, trailingPunctuation } = splitTrailingPunctuation(matchText)

    if (linkText) {
      segments.push({ type: 'link', text: linkText, href: toHref(linkText) })
    }
    appendText(segments, trailingPunctuation)

    cursor = matchIndex + matchText.length
  }

  appendText(segments, text.slice(cursor))
  return segments
}

export const parseVaultDescriptionLinks = (description: string): VaultDescriptionSegment[] => {
  const segments: VaultDescriptionSegment[] = []
  let cursor = 0

  for (const match of description.matchAll(markdownLinkRegex)) {
    const matchIndex = match.index ?? 0
    const text = match[1]
    const href = match[2]

    if (!text || !href) continue

    segments.push(...parseRawLinks(description.slice(cursor, matchIndex)))
    segments.push({ type: 'link', text, href })

    cursor = matchIndex + match[0].length
  }

  segments.push(...parseRawLinks(description.slice(cursor)))
  return segments
}
